import { html, nothing } from "lit";
import type { SessionKeyPath } from "../chat/session-key-path.ts";
import type { LogEntry, LogLevel } from "../types.ts";
import { DEFAULT_LOG_AREAS } from "../../../../src/logging/log-scope.js";
import { renderLogsScopeBar, type LogsScopeBarProps } from "./logs-scope-bar.ts";

const LEVELS: LogLevel[] = ["trace", "debug", "info", "warn", "error", "fatal"];

export type LogsProps = {
  loading: boolean;
  error: string | null;
  file: string | null;
  entries: LogEntry[];
  displayedCount: number;
  loadedCount: number;
  matchedCount: number | null;
  scannedCount: number | null;
  filterText: string;
  levelFilters: Record<LogLevel, boolean>;
  autoFollow: boolean;
  truncated: boolean;
  scopeBar: LogsScopeBarProps;
  areaFilters: string[];
  includePlatform: boolean;
  viewAll: boolean;
  expandedRaw: string | null;
  /**
   * JAISIU-types-sweep: short label rendered above the log table so the
   * operator knows which source file / socket these entries came from.
   * `app-render.ts` passes `"~/.openclaw"`, `"~/.jaisiu"`, `"gateway"`,
   * etc. depending on the active tab. Optional on the type so existing
   * callers (logs.ts itself, the help-content placeholders, etc.) don't
   * have to thread a value through.
   */
  sourceLabel?: string;
  /**
   * JAISIU-types-sweep: number of source files aggregated into the
   * log buffer. Mirrors the `fileCount` adapter field in
   * controllers/session-logs.ts. Optional — default 0 hides the chip
   * entirely when the source is a single file.
   */
  fileCount?: number | null;
  onFilterTextChange: (next: string) => void;
  onLevelToggle: (level: LogLevel, enabled: boolean) => void;
  onToggleAutoFollow: (next: boolean) => void;
  onToggleArea: (area: string, enabled: boolean) => void;
  onToggleIncludePlatform: (next: boolean) => void;
  onRefresh: () => void;
  onExport: (lines: string[], label: string) => void;
  onScroll: (event: Event) => void;
  onToggleRowExpand: (raw: string | null) => void;
};

function formatTime(value?: string | null) {
  if (!value) {
    return "";
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return date.toLocaleTimeString();
}

function scopeSummary(entry: LogEntry): string {
  if (entry.sessionKey) {
    return entry.sessionKey;
  }
  const parts = [entry.scope, entry.agent, entry.kind, entry.id].filter(Boolean);
  return parts.length > 0 ? parts.join(":") : "—";
}

function formatPrettyJson(raw: string): string {
  try {
    return JSON.stringify(JSON.parse(raw), null, 2);
  } catch {
    return raw;
  }
}

export function renderLogs(props: LogsProps) {
  const exportLabel = props.displayedCount < props.loadedCount ? "filtered" : "visible";

  return html`
    <section class="card logs-explorer">
      <div class="row" style="justify-content: space-between;">
        <div>
          <div class="card-title">Logs</div>
          <div class="card-sub">
            Jaisiu gateway file logs (<span class="mono">openclaw-*.log</span>). Use
            <strong>All gateway logs</strong> for the full file, or <strong>Session stream</strong>
            to match Chat.
          </div>
        </div>
        <div class="row" style="gap: 8px;">
          <button class="btn" ?disabled=${props.loading} @click=${props.onRefresh}>
            ${props.loading ? "Loading…" : "Refresh"}
          </button>
          <button
            class="btn"
            ?disabled=${props.displayedCount === 0}
            @click=${() =>
              props.onExport(
                props.entries.map((entry) => entry.raw),
                exportLabel,
              )}
          >
            Export ${exportLabel}
          </button>
        </div>
      </div>

      <div class="muted" style="margin-top: 8px; font-size: 12px;">
        Showing ${props.displayedCount} of ${props.loadedCount} loaded
        ${
          props.matchedCount != null && props.scannedCount != null
            ? html` · ${props.matchedCount} matched of ${props.scannedCount} scanned`
            : nothing
        }
      </div>

      ${renderLogsScopeBar(props.scopeBar)}

      <div class="chip-row" style="margin-top: 12px; ${props.viewAll ? "opacity: 0.55;" : ""}">
        <span class="muted" style="font-size: 12px; align-self: center; margin-right: 6px;">Area</span>
        ${[...DEFAULT_LOG_AREAS].map((area) => {
          const enabled = props.areaFilters.includes(area);
          return html`
            <label class="chip log-chip ${enabled ? "active" : ""}">
              <input
                type="checkbox"
                .checked=${enabled}
                ?disabled=${props.viewAll}
                @change=${(e: Event) =>
                  props.onToggleArea(area, (e.target as HTMLInputElement).checked)}
              />
              <span>${area}</span>
            </label>
          `;
        })}
      </div>

      <div class="filters" style="margin-top: 14px;">
        <label class="field" style="min-width: 220px;">
          <span>Filter</span>
          <input
            .value=${props.filterText}
            @input=${(e: Event) => props.onFilterTextChange((e.target as HTMLInputElement).value)}
            placeholder="Search logs"
          />
        </label>
        <label class="field checkbox">
          <span>Auto-follow</span>
          <input
            type="checkbox"
            .checked=${props.autoFollow}
            @change=${(e: Event) =>
              props.onToggleAutoFollow((e.target as HTMLInputElement).checked)}
          />
        </label>
        <label class="field checkbox">
          <span>Include platform logs</span>
          <input
            type="checkbox"
            .checked=${props.includePlatform}
            ?disabled=${props.viewAll}
            @change=${(e: Event) =>
              props.onToggleIncludePlatform((e.target as HTMLInputElement).checked)}
          />
        </label>
      </div>

      <div class="chip-row" style="margin-top: 12px;">
        ${LEVELS.map(
          (level) => html`
            <label class="chip log-chip ${level}">
              <input
                type="checkbox"
                .checked=${props.levelFilters[level]}
                @change=${(e: Event) =>
                  props.onLevelToggle(level, (e.target as HTMLInputElement).checked)}
              />
              <span>${level}</span>
            </label>
          `,
        )}
      </div>

      ${
        props.file
          ? html`<div class="muted" style="margin-top: 10px;">File: ${props.file}</div>`
          : nothing
      }
      ${
        props.truncated
          ? html`
              <div class="callout" style="margin-top: 10px">
                Log output truncated; showing latest chunk. Narrow scope or raise tail limits if needed.
              </div>
            `
          : nothing
      }
      ${
        props.error
          ? html`<div class="callout danger" style="margin-top: 10px;">${props.error}</div>`
          : nothing
      }

      <div class="log-stream logs-explorer__stream" style="margin-top: 12px;" @scroll=${props.onScroll}>
        ${
          props.displayedCount === 0
            ? html`
                <div class="muted" style="padding: 12px">
                  No log entries for this stream. Try widening scope, enabling platform logs, or selecting more
                  areas.
                </div>
              `
            : props.entries.map(
                (entry) => html`
                  <div
                    class="log-row logs-explorer__row ${
                      props.expandedRaw === entry.raw ? "logs-explorer__row--expanded" : ""
                    }"
                    data-testid="webchat-log-entry"
                    data-log-message=${entry.message ?? entry.raw}
                    @click=${() =>
                      props.onToggleRowExpand(props.expandedRaw === entry.raw ? null : entry.raw)}
                  >
                    <div class="log-time mono">${formatTime(entry.time)}</div>
                    <div class="log-level ${entry.level ?? ""}">${entry.level ?? ""}</div>
                    <div class="log-subsystem mono">${entry.area ?? entry.subsystem ?? ""}</div>
                    <div class="log-subsystem mono logs-explorer__scope" title=${scopeSummary(entry)}>
                      ${scopeSummary(entry)}
                    </div>
                    <div class="log-message mono">${entry.message ?? entry.raw}</div>
                  </div>
                  ${
                    props.expandedRaw === entry.raw
                      ? html`
                          <pre
                            class="logs-explorer__detail mono"
                            data-testid="webchat-log-detail"
                          >${formatPrettyJson(entry.raw)}</pre>
                        `
                      : nothing
                  }
                `,
              )
        }
      </div>
    </section>
  `;
}
